import { Package, User, MapPin, Navigation, FileText } from "lucide-react";
import { TASK_CATEGORIES } from "@shared/schema";

export type DomainKey = "order_item" | "recipient" | "address" | "drop_zone" | "other_requirements";
export type DomainState = null | "Good" | "Bad" | "Unclear" | "Override";
export type CriterionState = null | "correct" | "wrong" | "unclear" | "override";
export type EvidenceSignal = "verifies" | "error" | "inconclusive" | null;
export type EvidenceVerdict = "helpful" | "weak" | "excluded" | null;

export const DOMAIN_DISPLAY: Record<DomainKey, { label: string; icon: any }> = {
  order_item: { label: "Right Order", icon: Package },
  recipient: { label: "Right Recipient", icon: User },
  address: { label: "Right Address", icon: MapPin },
  drop_zone: { label: "Right Drop Zone", icon: Navigation },
  other_requirements: { label: "Other Requirements", icon: FileText },
};

export const CRITERION_TO_DOMAIN: Record<string, "order" | "location" | "requirements"> = {
  items: "order",
  signature: "order",
  quantity: "order",
  bagNotes: "order",
  address: "location",
  dropZone: "location",
  otherInstructions: "requirements",
  completionReqs: "requirements",
};

export const EVIDENCE_SOURCE_LABELS: Record<string, string> = {
  completionNotes: "Completion Notes",
  signatureText: "Signature Name",
  signatureUploadId: "Signature Image",
  photoUploadId: "Photo",
  aiVision: "Photo OCR",
  barcode: "Barcode Scan",
  agentEdit: "Manual Agent Edit",
};

export const EVIDENCE_FIELD_TO_ROW: Record<string, string> = {
  actualRecipient: "recipient",
  actualSignatureName: "signatureName",
  actualStreetNumber: "streetNumber",
  actualUnitNumber: "unitNumber",
  actualDzeCode: "dzeCode",
  recipient: "recipient",
  streetNumber: "streetNumber",
  unitNumber: "unitNumber",
  addressType: "addressType",
  dzePrimary: "dzePrimary",
  dzeSecondary: "dzeSecondary",
};

export function cycleDomainState(current: DomainState): DomainState {
  const cycle: DomainState[] = [null, "Good", "Bad", "Unclear", "Override"];
  return cycle[(cycle.indexOf(current) + 1) % cycle.length];
}

export function getDomainCriteriaMap(taskCat: number, _sigRequired: boolean): Record<DomainKey, string[]> {
  const map: Record<DomainKey, string[]> = {
    order_item: [],
    recipient: [],
    address: [],
    drop_zone: [],
    other_requirements: [],
  };
  switch (taskCat) {
    case TASK_CATEGORIES.CONNECT:
      map.order_item = ["items"];
      map.recipient = ["signature"];
      map.address = ["address"];
      map.drop_zone = ["dropZone"];
      break;
    case TASK_CATEGORIES.B2B:
      map.order_item = ["items"];
      map.recipient = ["signature"];
      map.address = ["address"];
      map.drop_zone = ["dropZone"];
      break;
    case TASK_CATEGORIES.REGULAR:
      map.order_item = ["items"];
      map.recipient = ["signature"];
      map.address = ["address"];
      map.drop_zone = ["dropZone"];
      break;
    case TASK_CATEGORIES.PICKUP:
      map.order_item = ["items", "quantity"];
      map.address = ["address"];
      break;
  }
  return map;
}

export function getDomainApplicability(taskCat: number, _sigRequired: boolean): Record<DomainKey, boolean> {
  return {
    order_item: taskCat === TASK_CATEGORIES.CONNECT || taskCat === TASK_CATEGORIES.B2B || taskCat === TASK_CATEGORIES.REGULAR || taskCat === TASK_CATEGORIES.PICKUP,
    recipient: taskCat === TASK_CATEGORIES.CONNECT || taskCat === TASK_CATEGORIES.B2B || taskCat === TASK_CATEGORIES.REGULAR,
    address: taskCat === TASK_CATEGORIES.CONNECT || taskCat === TASK_CATEGORIES.B2B || taskCat === TASK_CATEGORIES.REGULAR,
    drop_zone: taskCat === TASK_CATEGORIES.CONNECT || taskCat === TASK_CATEGORIES.B2B || taskCat === TASK_CATEGORIES.REGULAR,
    other_requirements: true,
  };
}

export function getDomainRequired(taskCat: number, sigRequired: boolean): Record<DomainKey, boolean> {
  const applicable = getDomainApplicability(taskCat, sigRequired);
  return {
    order_item: applicable.order_item,
    recipient: applicable.recipient,
    address: applicable.address,
    drop_zone: applicable.drop_zone,
    other_requirements: false,
  };
}

export function domainStateToBoolean(state: DomainState): boolean | null {
  if (state === "Good" || state === "Override") return true;
  if (state === "Bad" || state === "Unclear") return false;
  return null;
}

export function worstDomain(...states: DomainState[]): DomainState {
  const active = states.filter(s => s !== null);
  if (active.length === 0) return null;
  if (active.some(s => s === "Bad")) return "Bad";
  if (active.some(s => s === "Unclear")) return "Unclear";
  return "Good";
}

export function getActiveCriteria(taskCategory: number): { key: string; label: string }[] {
  const criteria: { key: string; label: string }[] = [];
  switch (taskCategory) {
    case TASK_CATEGORIES.CONNECT:
      criteria.push({ key: "items", label: "Item" }, { key: "signature", label: "Recipient" }, { key: "address", label: "Addr" }, { key: "dropZone", label: "DZ" });
      break;
    case TASK_CATEGORIES.B2B:
      criteria.push({ key: "items", label: "Item" }, { key: "signature", label: "Recipient" }, { key: "address", label: "Addr" }, { key: "dropZone", label: "DZ" });
      break;
    case TASK_CATEGORIES.REGULAR:
      criteria.push({ key: "items", label: "Item" }, { key: "signature", label: "Recipient" }, { key: "address", label: "Addr" }, { key: "dropZone", label: "DZ" });
      break;
    case TASK_CATEGORIES.PICKUP:
      criteria.push({ key: "items", label: "Items" }, { key: "quantity", label: "Qty" });
      break;
  }
  return criteria;
}

export function criterionToDomain(criterionKey: string): "order" | "location" | "requirements" | undefined {
  return CRITERION_TO_DOMAIN[criterionKey];
}

export function signalToVerdict(signal: "verifies" | "inconclusive" | "error"): "supports" | "contradicts" | "unclear" {
  if (signal === "verifies") return "supports";
  if (signal === "error") return "contradicts";
  return "unclear";
}

export function getStatusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Waiting": return "secondary";
    case "In Progress": return "default";
    case "Resolved": case "Good": return "outline";
    case "Needs Resolution": case "Bad": case "Unclear": return "destructive";
    case "Skipped": return "secondary";
    default: return "secondary";
  }
}

export type ActualEvidenceItem = {
  field: string;
  evidenceType: string;
  description?: string;
  photoRef?: string | number | null;
  confidence?: number | null;
  sourceValue?: string;
  extractedValue?: string | null;
};

export function getActualEvidence(d: any): ActualEvidenceItem[] {
  const raw = d?.actualEvidence;
  if (!Array.isArray(raw)) return [];
  return raw.map((e: any) => ({
    field: String(e?.field ?? e?.fieldKey ?? ""),
    evidenceType: (e?.evidenceType ?? e?.type ?? "unknown") as string,
    description: typeof e?.description === "string" ? e.description : undefined,
    photoRef: e?.photoRef ?? e?.photoIndex ?? e?.photo_index ?? null,
    confidence: typeof e?.confidence === "number" ? e.confidence : null,
    sourceValue: typeof e?.sourceValue === "string" ? e.sourceValue : undefined,
    extractedValue: e?.extractedValue ?? null,
  })).filter((e) => e.field !== "photoEvidence");
}

export function getSourceLabel(item: ActualEvidenceItem): string {
  const base = EVIDENCE_SOURCE_LABELS[item.evidenceType] || item.evidenceType;
  if (item.evidenceType === "completionNotes") {
    if (item.sourceValue?.startsWith("destination.")) return "Destination Notes";
    if (item.sourceValue?.startsWith("task.recipients")) return "Task Assignment";
    if (item.sourceValue?.startsWith("destination metadata")) return "Task Metadata";
  }
  return base;
}

export function formatPhotoRef(photoRef: string | number | null | undefined): string {
  if (photoRef == null) return "";
  if (typeof photoRef === "string" && photoRef.length > 8) return `Photo ${photoRef.slice(0, 6)}…`;
  if (typeof photoRef === "number") return `Photo ${photoRef + 1}`;
  if (typeof photoRef === "string" && /^\d+$/.test(photoRef)) return `Photo ${Number(photoRef) + 1}`;
  return `Photo ${photoRef}`;
}

export function formatConfidence(confidence: number | null | undefined): string {
  if (typeof confidence !== "number" || !Number.isFinite(confidence)) return "";
  const pct = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
  return `${pct}%`;
}

export function buildEvidenceByField(allEvidence: ActualEvidenceItem[]): Record<string, ActualEvidenceItem[]> {
  const evidenceByField: Record<string, ActualEvidenceItem[]> = {};
  for (const ev of allEvidence) {
    const rowKey = EVIDENCE_FIELD_TO_ROW[ev.field] ?? ev.field;
    if (!evidenceByField[rowKey]) evidenceByField[rowKey] = [];
    evidenceByField[rowKey].push(ev);
  }
  for (const key of Object.keys(evidenceByField)) {
    evidenceByField[key].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  }
  return evidenceByField;
}

export function normalizeFieldValue(v: string | null | undefined): string {
  return (v || "").trim().toLowerCase()
    .replace(/\s*\(.*?\)\s*/g, " ")  // strip parenthetical content e.g. "(buzz 3727)"
    .replace(/^#\s*/, "")
    .replace(/^apt\.?\s*/i, "")
    .replace(/^unit\s*/i, "")
    .replace(/^ste\.?\s*/i, "")
    .replace(/^suite\s*/i, "")
    .trim();
}

export function fieldMatchStatus(exp: string | null | undefined, act: string | null | undefined): "neutral" | "pending" | "match" | "mismatch" {
  if (!exp && !act) return "neutral";
  if (!act) return "pending";
  if (normalizeFieldValue(exp) === normalizeFieldValue(act)) return "match";
  return "mismatch";
}
